import { useCallback, useSyncExternalStore } from "react";

/**
 * A flag that survives a reload: "has this person seen X". Browser storage, like the
 * What's-new menu's seen marker, so it is per browser and not per account: a person
 * on a second device sees an introduction once more. Storage can throw (private
 * windows, blocked site data), and then the flag only lasts for the session.
 *
 * Every component reading the same key shares one value. An introduction that
 * moves from one control to the next must not start over on the next one, and a
 * per-component copy would do exactly that.
 */

// Shared across components by design: one value per key, read by many (see above).
const sessionSeen = new Set<string>();
const listeners = new Set<() => void>();

function readSeen(key: string): boolean {
  if (sessionSeen.has(key)) return true;
  try {
    return localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Forget the session's own memory. For tests; storage is the caller's to clear. */
export function resetSeenOnceForTests(): void {
  sessionSeen.clear();
  listeners.forEach((listener) => listener());
}

export function useSeenOnce(key: string): { seen: boolean; markSeen: () => void } {
  const seen = useSyncExternalStore(
    subscribe,
    () => readSeen(key),
    () => false,
  );
  const markSeen = useCallback(() => {
    sessionSeen.add(key);
    try {
      localStorage.setItem(key, new Date().toISOString());
    } catch {
      // Session-only: sessionSeen already hides it until the next load.
    }
    listeners.forEach((listener) => listener());
  }, [key]);
  return { seen, markSeen };
}
